// خواندن فیش حقوقی
// props: { documentId, onClose, onDone }
import React, { useState, useRef } from 'react';
import { ui } from '../../components/ListPage';
import WarningDialog from '../../components/WarningDialog';
import { jsonOf, netErr } from '../../utils/http';

const toFa = n => String(n ?? '').replace(/[0-9]/g, d => '۰۱۲۳۴۵۶۷۸۹'[d]);
const fmtAmt = n => (Number(n || 0) ? Number(n).toLocaleString('fa-IR') : '•');

const API = (path, opts = {}) => fetch(`/api/tadarokat/hazineh-detail${path}`, {
  headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('access')}` },
  ...opts,
}).then(jsonOf, netErr);

const s = {
  overlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' },
  modal: { background: '#fff', borderRadius: '12px', width: '90%', maxWidth: '800px', maxHeight: '85vh', display: 'flex', flexDirection: 'column', boxShadow: '0 8px 32px rgba(0,0,0,0.25)', direction: 'rtl' },
  header: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 16px', borderBottom: '1px solid #e6e8ef', background: '#1a2f50', borderRadius: '12px 12px 0 0' },
  title: { color: '#fff', fontWeight: 700, fontSize: '14px' },
  body: { padding: '16px', overflow: 'auto', flex: 1 },
  footer: { display: 'flex', gap: '8px', justifyContent: 'flex-start', padding: '12px 16px', borderTop: '1px solid #e6e8ef' },
  row: { display: 'flex', gap: '12px', flexWrap: 'wrap', marginBottom: '12px', alignItems: 'flex-end' },
  label: { fontSize: '12px', fontWeight: 700, color: '#1a2f50', marginBottom: '4px' },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: '12px', textAlign: 'right' },
  th: { padding: '6px 8px', background: '#1a2f50', color: '#fff', fontWeight: 700, border: '1px solid #ccc', whiteSpace: 'nowrap' },
  td: { padding: '5px 8px', border: '1px solid #eee' },
  tdNum: { padding: '5px 8px', border: '1px solid #eee', textAlign: 'left', direction: 'ltr' },
  btn: { padding: '8px 18px', background: 'linear-gradient(135deg,#1a237e,#283593)', color: '#fff', border: 'none', borderRadius: '8px', cursor: 'pointer', fontFamily: 'inherit', fontWeight: 700, fontSize: '13px' },
  btnGreen: { padding: '8px 18px', background: 'linear-gradient(135deg,#2e7d32,#388e3c)', color: '#fff', border: 'none', borderRadius: '8px', cursor: 'pointer', fontFamily: 'inherit', fontWeight: 700, fontSize: '13px' },
  errRow: { background: '#fff3f3' },
  okRow: { background: '#f0fff0' },
  msg: { padding: '12px', borderRadius: '8px', marginBottom: '12px', fontSize: '13px' },
};

function LookupField({ label, value, displayText, kind, onChange }) {
  const [text, setText] = useState(displayText || '');
  const [list, setList] = useState([]);
  const [open, setOpen] = useState(false);
  const timer = useRef(null);

  const search = (q) => {
    setText(q);
    clearTimeout(timer.current);
    timer.current = setTimeout(async () => {
      const j = await API(`/read-payrol/lookup/?kind=${kind}&q=${encodeURIComponent(q)}`);
      setList(j.ok ? j.data : []);
      setOpen(true);
    }, 300);
  };

  return (
    <div style={{ flex: 1, minWidth: '200px' }}>
      <div style={s.label}>{label}</div>
      <div style={{ position: 'relative' }}>
        <div style={{ display: 'flex', gap: '4px' }}>
          <input value={text} placeholder={`جستجوی ${label}...`}
            onChange={e => search(e.target.value)} onFocus={() => search(text)}
            onBlur={() => setTimeout(() => setOpen(false), 200)}
            style={{ ...ui.input, flex: 1 }} />
          {value ? <button type="button" onClick={() => { onChange(0, ''); setText(''); }}
            style={{ ...ui.btnCancel, padding: '4px 10px' }}>✕</button> : null}
        </div>
        {open && list.length > 0 && (
          <div style={{ position: 'absolute', zIndex: 50, background: '#fff', border: '1px solid #ccc', borderRadius: '8px', maxHeight: '200px', overflowY: 'auto', width: '100%', boxShadow: '0 6px 18px rgba(0,0,0,0.15)' }}>
            {list.map(o => (
              <div key={o.id} onMouseDown={() => { onChange(o.id, o.display || o.title); setText(o.display || o.title); setOpen(false); }}
                style={{ padding: '6px 10px', cursor: 'pointer', fontSize: '12px', borderBottom: '1px solid #f0f0f0' }}>
                {toFa(o.display || o.title)}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default function ReadPayrolHazineh({ documentId, onClose, onDone }) {
  const [fileContent, setFileContent] = useState('');
  const [fileName, setFileName] = useState('');
  const [rows, setRows] = useState(null);
  const [total, setTotal] = useState(0);

  const [agreementId, setAgreementId] = useState(0);
  const [agreementText, setAgreementText] = useState('');
  const [creditId, setCreditId] = useState(0);
  const [creditText, setCreditText] = useState('');
  const [programId, setProgramId] = useState(0);
  const [programText, setProgramText] = useState('');

  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState('');
  const [msgType, setMsgType] = useState('');  // 'ok' | 'err' | 'warn'
  const [confirmInsert, setConfirmInsert] = useState(false);

  const fileRef = useRef(null);

  const handleFile = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setFileName(file.name);
    const reader = new FileReader();
    reader.onload = (ev) => {
      setFileContent(ev.target.result);
      setRows(null);
      setMsg('');
    };
    reader.readAsText(file, 'utf-8');
  };

  const parseFile = async () => {
    if (!fileContent.trim()) { setMsg('لطفاً فایل TXT را انتخاب کنید'); setMsgType('err'); return; }
    setLoading(true);
    setMsg('');
    try {
      const j = await API('/read-payrol/parse/', {
        method: 'POST',
        body: JSON.stringify({ file_content: fileContent }),
      });
      if (j.ok) {
        setRows(j.rows);
        setTotal(j.total);
        const errCount = j.rows.filter(r => r.error).length;
        if (errCount > 0) {
          setMsg(`${toFa(j.rows.length)} ردیف خوانده شد — ${toFa(errCount)} ردیف خطادار`);
          setMsgType('warn');
        } else {
          setMsg(`${toFa(j.rows.length)} ردیف خوانده شد — جمع: ${fmtAmt(j.total)} ریال`);
          setMsgType('ok');
        }
      } else {
        setMsg(j.msg || 'خطا در خواندن فایل');
        setMsgType('err');
      }
    } catch (e) {
      setMsg(String(e));
      setMsgType('err');
    }
    setLoading(false);
  };

  const doInsert = async () => {
    setConfirmInsert(false);
    if (!agreementId) { setMsg('لطفاً موافقتنامه را انتخاب کنید'); setMsgType('err'); return; }
    if (!programId) { setMsg('لطفاً برنامه را انتخاب کنید'); setMsgType('err'); return; }
    if (!creditId) { setMsg('لطفاً اعتبار را انتخاب کنید'); setMsgType('err'); return; }
    setLoading(true);
    setMsg('');
    try {
      const j = await API(`/documents/${documentId}/read-payrol/`, {
        method: 'POST',
        body: JSON.stringify({
          file_content: fileContent,
          agreement_id: agreementId,
          program_id: programId,
          credit_id: creditId,
        }),
      });
      if (j.ok) {
        let m = `${toFa(j.inserted)} ردیف از ${toFa(j.total_lines)} ردیف درج شد — جمع: ${fmtAmt(j.total_amount)} ریال`;
        if (j.warnings && j.warnings.length > 0) {
          m += '\n⚠ ' + j.warnings.join('\n⚠ ');
        }
        setMsg(m);
        setMsgType('ok');
        if (onDone) onDone();
      } else {
        setMsg(j.msg || 'خطا در درج');
        setMsgType('err');
      }
    } catch (e) {
      setMsg(String(e));
      setMsgType('err');
    }
    setLoading(false);
  };

  const validRows = rows ? rows.filter(r => !r.error) : [];
  const canInsert = validRows.length > 0 && agreementId && programId && creditId;

  return (
    <div style={s.overlay}>
      <div style={s.modal}>
        <div style={s.header}>
          <div style={s.title}>خواندن فیش حقوقی — سند {toFa(documentId)}</div>
          <button onClick={onClose} style={{ ...ui.closeBtn, color: '#fff' }}>✕</button>
        </div>

        <div style={s.body}>
          {/* انتخاب فایل */}
          <div style={s.row}>
            <div style={{ flex: 1 }}>
              <div style={s.label}>فایل TXT (کاما جداکننده: شماره هزینه، مبلغ)</div>
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                <input type="file" accept=".txt,.csv" ref={fileRef} onChange={handleFile}
                  style={{ fontSize: '12px' }} />
                {fileName && <span style={{ fontSize: '12px', color: '#666' }}>{fileName}</span>}
              </div>
            </div>
            <button style={s.btn} onClick={parseFile} disabled={loading || !fileContent}>
              {loading ? '⏳' : 'خواندن فایل'}
            </button>
          </div>

          {/* پیام */}
          {msg && (
            <div style={{
              ...s.msg,
              background: msgType === 'ok' ? '#e8f5e9' : msgType === 'warn' ? '#fff3e0' : '#ffebee',
              color: msgType === 'ok' ? '#2e7d32' : msgType === 'warn' ? '#e65100' : '#c62828',
              whiteSpace: 'pre-line',
            }}>
              {msg}
            </div>
          )}

          {/* جدول ردیف‌ها */}
          {rows && rows.length > 0 && (
            <div style={{ marginBottom: '16px', maxHeight: '300px', overflow: 'auto' }}>
              <table style={s.table}>
                <thead>
                  <tr>
                    <th style={s.th}>سطر</th>
                    <th style={s.th}>شماره هزینه</th>
                    <th style={s.th}>مبلغ</th>
                    <th style={s.th}>شرح هزینه</th>
                    <th style={s.th}>وضعیت</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r, i) => (
                    <tr key={i} style={r.error ? s.errRow : s.okRow}>
                      <td style={s.td}>{toFa(r.line)}</td>
                      <td style={s.tdNum}>{r.hz_no != null ? toFa(r.hz_no) : '—'}</td>
                      <td style={s.tdNum}>{r.amount != null ? fmtAmt(r.amount) : '—'}</td>
                      <td style={s.td}>{r.hazineh_remark || '—'}</td>
                      <td style={s.td}>{r.error ? <span style={{ color: '#c62828' }}>❌ {r.error}</span> : '✅'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div style={{ padding: '8px', fontWeight: 700, fontSize: '13px' }}>
                جمع مبالغ: {fmtAmt(total)} ریال — ردیف‌های معتبر: {toFa(validRows.length)}
              </div>
            </div>
          )}

          {/* lookups */}
          {rows && validRows.length > 0 && (
            <div style={s.row}>
              <LookupField label="موافقتنامه" value={agreementId} displayText={agreementText} kind="agreement"
                onChange={(id, txt) => { setAgreementId(id); setAgreementText(txt); }} />
              <LookupField label="اعتبار" value={creditId} displayText={creditText} kind="credit"
                onChange={(id, txt) => { setCreditId(id); setCreditText(txt); }} />
              <LookupField label="برنامه" value={programId} displayText={programText} kind="program"
                onChange={(id, txt) => { setProgramId(id); setProgramText(txt); }} />
            </div>
          )}
        </div>

        <div style={s.footer}>
          {canInsert && (
            <button style={s.btnGreen} onClick={() => setConfirmInsert(true)} disabled={loading}>
              درج در سند ({toFa(validRows.length)} ردیف)
            </button>
          )}
          <button style={{ ...s.btn, background: '#757575' }} onClick={onClose}>بستن</button>
        </div>
      </div>

      {confirmInsert && (
        <WarningDialog
          title="تایید درج"
          lines={[`آیا از درج ${toFa(validRows.length)} ردیف با جمع ${fmtAmt(total)} ریال مطمئن هستید؟`]}
          confirmLabel="بلی، درج شود"
          cancelLabel="خیر"
          tone="warning"
          onConfirm={doInsert}
          onCancel={() => setConfirmInsert(false)}
        />
      )}
    </div>
  );
}
